import {
    Component,
    ElementRef,
    EventEmitter,
    forwardRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    Output
} from "@angular/core";
import {FUIAbstractPill} from "../../framework/abstract-pill";
import {NG_VALUE_ACCESSOR} from "@angular/forms";
import {FUIOptionsService, SelectionEvent} from "../../framework/options.service";
import {FUIEventsService} from "../../../events/events.service";
import {Subscription} from "rxjs";

@Component({
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: forwardRef(() => FUIAutocompletePillComponent),
            multi: true
        }
    ],
    selector: "fui-autocomplete-pill",
    styleUrls: ["./autocomplete-pill.component.scss"],
    templateUrl: "./autocomplete-pill.component.html",
    standalone: false
})
export class FUIAutocompletePillComponent extends FUIAbstractPill implements OnInit, OnDestroy {
    @Input() public field: string;
    @Input() public key: string;
    @Input() public options: Array<any>;
    @Output() public filtered$: EventEmitter<string> = new EventEmitter<string>();

    private subscriptions: Subscription = new Subscription();

    constructor(public elementRef: ElementRef,
                private eventsService: FUIEventsService,
                protected optionsService: FUIOptionsService) {
        super();
        this.popped = false;
    }

    private _popped: boolean;

    /**
     * Get the popped value, used for
     * determining when the popup will be display
     * @returns {boolean}
     */
    get popped(): boolean {
        return this._popped;
    }

    /**
     * Set the popped value, used for
     * determining when the popup will be display
     * @param {boolean} popped
     */
    set popped(popped: boolean) {
        if (this._popped !== popped) {
            this._popped = popped;
        }
    }

    private _query: string;

    /**
     * Get the query value
     * @returns {string}
     */
    get query(): string {
        return this._query;
    }

    /**
     * Set the query and associated popup actions
     * @param {string} query
     */
    set query(query: string) {
        if (this._query !== query) {
            this._query = query;
            this.filtered$.emit(this._query);

            if (this._query) {
                if (this.focussed) {
                    if (this.value) {
                        if (this._query != this.value[this.key]) {
                            this.value = null;
                        }
                    }
                    this.popped = true;
                }
            }
            else {
                this.popped = false;
            }
        }
    }

    /**
     * Get the model value
     * @returns {string}
     */
    override get value(): any {
        return this._value;
    }

    /**
     * Set the model value
     * @param value
     */
    @Input()
    override set value(value: any) {
        if (value !== this._value) {
            this._value = value;
            this.onChange(value);
            this.optionsService.selection = this._value;
            this.valueChange.emit(value);
            if (this.value) {
                this.query = this.value[this.key];
            }
        }
    }

    ngOnInit(): void {
        this.subscriptions.add(this.eventsService.click$.subscribe(
            (click) => {
                if (this.popped && !this.elementRef.nativeElement.contains(click.target)) {
                    this.popped = false;
                }
            }
        ));
    }

    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }


    /**
     * Perform events after a click on the host
     * @param element
     */
    @HostListener("click")
    public override click(): void {
        this.focus();
    }

    /**
     * Set focus to the element and check if the popup should
     * show
     * @param element
     */
    @HostListener("focus")
    public override focus(): void {
        if (this.focussed) {
            this.popped = !!this.query;
        } else {
            this.input.nativeElement.focus();
            this.focussed = true;
            this.focussed$.emit(this.focussed);
            this.popped = false;
        }
    }

    /**
     * If the user presses the enter or space key,
     * select the first option in the list and set it
     * as the value and query, then close the popup.
     * @param {KeyboardEvent} event
     */
    public keypress(keyboardEvent: KeyboardEvent): void {
        if (keyboardEvent.code === "Enter" || keyboardEvent.code === "Space" || keyboardEvent.key === "Enter" || keyboardEvent.key === " ") {
            if (this.options) {
                this.value = this.options[0];
                this.popped = false;
                keyboardEvent.preventDefault();
                keyboardEvent.stopPropagation();
            }
        }
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public empty(options: Array<any>): boolean {
        return !this.loading && (!options || options.length === 0);
    }

    /**
     * Check if the list of options is empty
     * @returns {boolean}
     */
    public filled(options: Array<any>): boolean {
        return options && options.length > 0;
    }

    /**
     * Toggle the popped boolean
     */
    public pop(): void {
        this.popped = !this.popped;
    }

    /**
     * Assign the selected value, and close the popup if the selection was made by the user
     * @param event
     */
    public select(event: SelectionEvent): void {
        this.value = event.selection;
        if (event.userSelection) {
            this.popped = false;
        }
    }

    /**
     * Perform the initial writing of the model
     * value if it is set at runtime
     * @param value
     */
    public override writeValue(value: any): void {
        this._value = value;

        if (this._value) {
            this.optionsService.selection = this._value;
            this.query = this._value[this.key];
            this.valued = true;
        }
        else {
            this.valued = false;
        }
    }
}
